/**
 * ポータル画面（船員アプリ側）の共通部品
 * デザインは Microsoft Viva Engage / Connections 風（紫系・グラデーション・白カード）。
 */
import React from 'react';
import { API_BASE, getAccessToken } from '../../services/api';

/** ポータルのブランド（白基調・高コントラスト・アクセントは青緑1色。グラデーションは使わない） */
export const PORTAL_GRADIENT = 'linear-gradient(135deg, #0f766e, #0c5f59)'; // ボタン用の控えめな濃淡のみ
export const PORTAL_ACCENT = '#0f766e';
export const PORTAL_ACCENT_DARK = '#0c5f59';
export const PORTAL_ACCENT_SOFT = '#e6f4f1';

/** 名前からアバター色（人ごとに色を変える。濃色＋白文字で視認性を確保） */
const AVATAR_COLORS = ['#0f766e', '#1d4ed8', '#9333ea', '#be185d', '#b45309', '#15803d', '#0369a1', '#7c2d12'];
export function avatarColor(name: string | null | undefined): string {
  const s = name ?? '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}
export const Avatar: React.FC<{ name: string | null | undefined; size?: number }> = ({ name, size = 34 }) => (
  <span style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: avatarColor(name), color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.36, fontWeight: 700, userSelect: 'none',
  }}>
    {(name ?? '？').replace(/\s/g, '').slice(0, 2)}
  </span>
);

/** 認証つきファイルURL（新しいタブで開く用。requireAuth は ?token= を受け付ける） */
export function fileUrl(path: string): string {
  return `${API_BASE}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(getAccessToken() ?? '')}`;
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export const inputStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc',
  padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
  boxSizing: 'border-box', color: '#0f172a',
};
export const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 };

export const PillBadge: React.FC<{ color: string; bg: string; children: React.ReactNode }> = ({ color, bg, children }) => (
  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: bg, color, whiteSpace: 'nowrap' }}>{children}</span>
);

export const STATUS_PILL: Record<string, { label?: string; color: string; bg: string }> = {
  pending:  { color: '#b45309', bg: '#fef3c7' },
  officer:  { color: '#7c3aed', bg: '#ede9fe' },
  checking: { color: '#0f766e', bg: '#e6f4f1' },
  settled:  { color: '#15803d', bg: '#dcfce7' },
  approved: { color: '#15803d', bg: '#dcfce7' },
  rejected: { color: '#dc2626', bg: '#fee2e2' },
  withdrawn:{ color: '#64748b', bg: '#f1f5f9' },
  deleted:  { color: '#64748b', bg: '#f1f5f9' },
};

/** ごく簡易なMarkdown表示（見出し・箇条書き・太字・段落のみ） */
export const MarkdownView: React.FC<{ text: string }> = ({ text }) => {
  const lines = (text ?? '').split(/\r?\n/);
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  const inline = (s: string): React.ReactNode => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>);
  };
  const flushList = (key: number) => {
    if (list.length === 0) return;
    out.push(
      <ul key={`ul-${key}`} style={{ margin: '4px 0 10px', paddingLeft: 20 }}>
        {list.map((li, i) => <li key={i} style={{ margin: '3px 0', fontSize: 14, color: '#1f2937', lineHeight: 1.7 }}>{inline(li)}</li>)}
      </ul>
    );
    list = [];
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) { list.push(line.replace(/^\s*[-*]\s+/, '')); return; }
    flushList(i);
    if (line.startsWith('### ')) out.push(<h4 key={i} style={{ margin: '12px 0 4px', fontSize: 14.5, color: '#0f172a' }}>{inline(line.slice(4))}</h4>);
    else if (line.startsWith('## ')) out.push(<h3 key={i} style={{ margin: '14px 0 4px', fontSize: 15.5, color: '#0f172a' }}>{inline(line.slice(3))}</h3>);
    else if (line.startsWith('# ')) out.push(<h2 key={i} style={{ margin: '4px 0 8px', fontSize: 17, color: '#0f172a' }}>{inline(line.slice(2))}</h2>);
    else if (line.trim() === '') out.push(<div key={i} style={{ height: 6 }} />);
    else out.push(<p key={i} style={{ margin: '2px 0', fontSize: 14, color: '#1f2937', lineHeight: 1.75 }}>{inline(line)}</p>);
  });
  flushList(lines.length + 1);
  return <div>{out}</div>;
};


/** 空状態（イラストつき）。素材はユーザー提供のイラスト素材集から */
export const EmptyState: React.FC<{ img: string; text: string; size?: number }> = ({ img, text, size = 140 }) => (
  <div style={{ textAlign: 'center', padding: '28px 10px' }}>
    <img src={img} alt="" style={{ width: size, maxWidth: '65%' }} />
    <div style={{ fontSize: 13.5, color: '#50545b', marginTop: 10, fontWeight: 700 }}>{text}</div>
  </div>
);
